import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import React, { useMemo, useRef, useState } from 'react';
import { HighchartsReact } from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { getPtpsOverviewChartOptions } from './chartOptions';
import { getPtpsOverviewCards } from '../../utils';
import { formatAmount, formatNumber } from '@cp/src/utils/commonUtils';
import cx from 'classnames';
import styles from './chart.module.scss';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';

const PtpOverViews: React.FC<{ loading: boolean }> = ({ loading }) => {
  const ptpsOverviewMTD = useSelector(
    (state: RootState) => state.telePerformanceDashboard.ptpOverview
  );
  const chartComponentRef = useRef(null);
  const { chartOptions, overviewCards } = useMemo(() => {
    const chartOptions = getPtpsOverviewChartOptions(ptpsOverviewMTD);
    const overviewCards = getPtpsOverviewCards(ptpsOverviewMTD);
    return { chartOptions, overviewCards };
  }, [
    ptpsOverviewMTD?.brokenPtp?.amount,
    ptpsOverviewMTD?.brokenPtp?.count,

    ptpsOverviewMTD?.todayPtp?.amount,
    ptpsOverviewMTD?.todayPtp?.count,

    ptpsOverviewMTD?.futurePtp?.amount,
    ptpsOverviewMTD?.futurePtp?.count,

    ptpsOverviewMTD?.noPtp?.amount,
    ptpsOverviewMTD?.noPtp?.count,

    ptpsOverviewMTD?.totalUnpaidEmiAmount,
    ptpsOverviewMTD?.totalUnpaidCases
  ]);

  return (
    <SuspenseLoader
      loading={loading}
      fallBack={
        <div className={cx(styles.card, 'min-w-[28%] height-[90%]')}>
          <LineLoader
            childrenType="DISPLAY"
            lineWidth={100}
            numberOfLines={1}
            lineStyle={cx(styles.loader, styles.loaderRoundedTop)}
          />
          <LineLoader
            childrenType="DISPLAY"
            lineWidth={100}
            numberOfLines={9}
            lineStyle={styles.loader}
          />
          <LineLoader
            childrenType="DISPLAY"
            lineWidth={100}
            numberOfLines={1}
            lineStyle={cx(styles.loader, styles.loaderRoundedBottom)}
          />
        </div>
      }
    >
      <div className={cx('flex flex-col p-4 gap-3', styles.card)}>
        <Typography variant="h6" color="var(--navi-color-gray-c2)">
          PTPs Overview MTD{' '}
        </Typography>
        <div className="flex items-center justify-center px-6">
          <HighchartsReact ref={chartComponentRef} highcharts={Highcharts} options={chartOptions} />
        </div>
        <div className="grid grid-cols-2 gap-[10px]">
          {overviewCards?.map(item => {
            return (
              <div
                key={item.key}
                className={cx(
                  'rounded-lg border border-solid border-[var(--greyscale-light)] bg-[var(--bg-primary)] p-[6px]',
                  styles.cardHover
                )}
              >
                <div className="flex gap-[6px] items-center">
                  <div className="w-3 h-3 rounded" style={{ background: item?.color }} />
                  <Typography
                    variant="h6"
                    color="var(--navi-color-gray-c3)"
                    className="font-normal"
                  >
                    {item?.title}
                  </Typography>
                </div>
                <div className="flex mt-3 items-end gap-1">
                  <Typography variant="h2" color="var(--navi-color-gray-c2)" className="leading-6">
                    {formatNumber(item?.count ?? 0)}
                  </Typography>
                  {item?.amount ? (
                    <Typography variant="h6" color="var(--navi-color-gray-c3)">
                      ({formatAmount(item?.amount)})
                    </Typography>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SuspenseLoader>
  );
};

export default PtpOverViews;
